(function () {
  if (window.__searchTypewriterInit) {
    return;
  }

  window.__searchTypewriterInit = true;

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  );


  /* =========================================================
     GET PHRASES
     ========================================================= */

  function getPhrases(input) {
    const multiPhraseData =
      input.dataset.typewriterPhrases || "";

    /*
     * New version:
     * phrases come from the Theme Editor textarea.
     */
    if (multiPhraseData.trim()) {
      let value = multiPhraseData;

      /*
       * Liquid sends the textarea using | json,
       * so decode it safely.
       */
      try {
        const parsed = JSON.parse(value);

        if (typeof parsed === "string") {
          value = parsed;
        }

        if (Array.isArray(parsed)) {
          return parsed
            .map((phrase) => String(phrase).trim())
            .filter(Boolean);
        }
      } catch (error) {
        /*
         * If for any reason it's not JSON,
         * treat it as normal multiline text.
         */
      }

      return String(value)
        .split(/\r?\n/)
        .map((phrase) => phrase.trim())
        .filter(Boolean);
    }


    /*
     * Backwards compatibility with the original Athens:
     * First phrase + Second phrase.
     */
    return [
      input.dataset.typewriterPrimary,
      input.dataset.typewriterSecondary
    ]
      .map((value) => (value || "").trim())
      .filter((value) => value.length > 0);
  }


  /* =========================================================
     TYPEWRITER CLASS
     ========================================================= */

  class Typewriter {
    constructor(input) {
      this.input = input;

      this.fallbackPlaceholder =
        input.getAttribute("placeholder") || "";

      const raw = getPhrases(input);

      this.rawPhrases = raw;

      /*
       * Array.from handles Unicode / emojis better
       * than normal string indexing.
       */
      this.phrases = raw.map((value) =>
        Array.from(value)
      );


      /* ===============================================
         SPEED SETTINGS
         =============================================== */

      this.typingSpeed =
        parseInt(
          input.dataset.typewriterSpeed,
          10
        ) || 70;

      this.deleteSpeed =
        parseInt(
          input.dataset.typewriterDeleteSpeed,
          10
        ) || 40;

      this.holdDuration =
        parseInt(
          input.dataset.typewriterPause,
          10
        ) || 1600;

      /*
       * Small random variation makes typing
       * feel less robotic.
       */
      this.typingVariation = 15;


      /* ===============================================
         INTERNAL STATE
         =============================================== */

      this.timeoutId = null;
      this.caretIntervalId = null;

      this.phraseIndex = 0;
      this.charIndex = 0;

      this.phase = "type";

      this.caretVisible = true;
      this.running = false;


      /* ===============================================
         EVENT BINDINGS
         =============================================== */

      this.handleFocus =
        this.handleFocus.bind(this);

      this.handleBlur =
        this.handleBlur.bind(this);

      this.handleInputChange =
        this.handleInputChange.bind(this);

      this.handleVisibilityChange =
        this.handleVisibilityChange.bind(this);
    }


    /* =====================================================
       INIT
       ===================================================== */

    init() {
      if (!this.phrases.length) {
        return;
      }


      /*
       * Reduced motion:
       * show first phrase without animation.
       */
      if (prefersReducedMotion.matches) {
        this.input.setAttribute(
          "placeholder",
          this.rawPhrases[0]
        );

        return;
      }


      this.input.addEventListener(
        "focus",
        this.handleFocus
      );

      this.input.addEventListener(
        "blur",
        this.handleBlur
      );

      this.input.addEventListener(
        "input",
        this.handleInputChange
      );

      document.addEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );


      /*
       * Start only when:
       *
       * - search is not focused
       * - customer hasn't typed anything
       */
      if (
        document.activeElement !== this.input &&
        !this.input.value
      ) {
        this.start();
      }
    }


    /* =====================================================
       FOCUS
       ===================================================== */

    handleFocus() {
      this.stop();

      this.input.setAttribute(
        "placeholder",
        this.fallbackPlaceholder
      );
    }


    /* =====================================================
       BLUR
       ===================================================== */

    handleBlur() {
      if (this.input.value) {
        return;
      }

      this.reset();
      this.start();
    }


    /* =====================================================
       INPUT CHANGE
       ===================================================== */

    handleInputChange() {
      /*
       * Stops immediately when customer types
       * OR Voice Search inserts a transcript.
       */
      if (this.input.value) {
        this.stop();
      }
    }


    /* =====================================================
       TAB VISIBILITY
       ===================================================== */

    handleVisibilityChange() {
      if (document.hidden) {
        this.stop({
          preservePlaceholder: true
        });

        return;
      }


      if (
        document.activeElement !== this.input &&
        !this.input.value
      ) {
        this.start();
      }
    }


    /* =====================================================
       RESET
       ===================================================== */

    reset() {
      this.phraseIndex = 0;
      this.charIndex = 0;
      this.phase = "type";
    }


    /* =====================================================
       START
       ===================================================== */

    start() {
      if (
        this.running ||
        !this.phrases.length
      ) {
        return;
      }


      this.running = true;

      this.startCaretBlink();

      this.tick();
    }


    /* =====================================================
       STOP
       ===================================================== */

    stop({
      preservePlaceholder = false
    } = {}) {
      this.running = false;


      if (this.timeoutId) {
        clearTimeout(
          this.timeoutId
        );

        this.timeoutId = null;
      }


      this.stopCaretBlink();


      if (
        !preservePlaceholder &&
        this.input
      ) {
        this.input.setAttribute(
          "placeholder",
          this.fallbackPlaceholder
        );
      }
    }


    /* =====================================================
       CARET
       ===================================================== */

    startCaretBlink() {
      this.caretVisible = true;

      this.stopCaretBlink();


      this.caretIntervalId =
        setInterval(() => {
          this.caretVisible =
            !this.caretVisible;

          this.render();
        }, 530);
    }


    stopCaretBlink() {
      if (this.caretIntervalId) {
        clearInterval(
          this.caretIntervalId
        );

        this.caretIntervalId = null;
      }
    }


    /* =====================================================
       RENDER
       ===================================================== */

    render() {
      if (
        !this.input ||
        !this.phrases.length
      ) {
        return;
      }


      const phrase =
        this.phrases[
          this.phraseIndex
        ];


      const visible =
        phrase
          .slice(
            0,
            this.charIndex
          )
          .join("");


      const showCaret =
        this.phase !== "delete" &&
        this.caretVisible;


      this.input.setAttribute(
        "placeholder",
        showCaret
          ? `${visible}│`
          : visible
      );
    }


    /* =====================================================
       TYPEWRITER ENGINE
       ===================================================== */

    tick() {
      if (!this.running) {
        return;
      }


      const phrase =
        this.phrases[
          this.phraseIndex
        ];


      /* =========================
         TYPE
         ========================= */

      if (this.phase === "type") {
        this.charIndex += 1;

        this.render();


        if (
          this.charIndex >=
          phrase.length
        ) {
          this.phase = "hold";

          this.schedule(
            this.holdDuration
          );
        }

        else {
          /*
           * Slight random human-like speed.
           */
          const variation =
            (
              Math.random() * 2 - 1
            ) *
            this.typingVariation;


          this.schedule(
            Math.max(
              15,
              this.typingSpeed +
                variation
            )
          );
        }

        return;
      }


      /* =========================
         HOLD
         ========================= */

      if (this.phase === "hold") {
        this.phase = "delete";

        this.schedule(
          this.deleteSpeed
        );

        return;
      }


      /* =========================
         DELETE
         ========================= */

      if (this.phase === "delete") {
        this.charIndex =
          Math.max(
            0,
            this.charIndex - 1
          );


        this.render();


        if (
          this.charIndex === 0
        ) {
          this.phase = "type";


          /*
           * Move to next phrase.
           *
           * When last phrase finishes,
           * start again from phrase 1.
           */
          this.phraseIndex =
            (
              this.phraseIndex + 1
            ) %
            this.phrases.length;


          this.schedule(350);
        }

        else {
          this.schedule(
            this.deleteSpeed
          );
        }
      }
    }


    /* =====================================================
       TIMER
       ===================================================== */

    schedule(delay) {
      if (this.timeoutId) {
        clearTimeout(
          this.timeoutId
        );
      }


      this.timeoutId =
        setTimeout(
          () => this.tick(),
          delay
        );
    }
  }


  /* =========================================================
     INITIALISE ALL
     ========================================================= */

  function initAll(root) {
    const scope =
      root || document;


    /*
     * Supports both:
     *
     * New unlimited phrases system
     * AND old Athens primary/secondary system.
     */
    const inputs =
      scope.querySelectorAll(
        [
          "input[data-typewriter-phrases]:not([data-typewriter-initialized])",
          "input[data-typewriter-primary]:not([data-typewriter-initialized])"
        ].join(",")
      );


    inputs.forEach((input) => {
      input.setAttribute(
        "data-typewriter-initialized",
        "true"
      );

      new Typewriter(
        input
      ).init();
    });
  }


  /* =========================================================
     DOM READY
     ========================================================= */

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      () => initAll()
    );
  }

  else {
    initAll();
  }


  window.addEventListener(
    "load",
    () => initAll()
  );


  /* =========================================================
     SHOPIFY THEME EDITOR
     ========================================================= */

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initAll(
        event.target
      );
    }
  );

})();